import mixpanel from 'mixpanel-browser'
import { useAuthStore } from 'src/stores/auth'
import { getCurrentInstance } from 'vue'
import { useRoute } from 'vue-router'

const MIXPANEL_TOKEN = import.meta.env.VITE_MIXPANEL_TOKEN
const isDev = import.meta.env.DEV
let isInitialized = false

// Internal/test orgs (org_status === 'Test Org', from the login response's
// Config.user_id.org_status) are excluded from Mixpanel in production builds only —
// the dev/test Mixpanel project is where this tracking itself gets exercised, so
// internal traffic there is expected, not noise. See docs/Behavioral_Analysis_Mixpanel.md §3b.
export function isInternalOrg(orgStatus) {
  return (
    import.meta.env.PROD &&
    import.meta.env.VITE_MIXPANEL_INCLUDE_TEST_ORG !== 'true' &&
    orgStatus === 'Test Org'
  )
}

export function initAnalytics() {
  if (isDev) return // temporarily disable analytics in development to avoid polluting data
  if (!MIXPANEL_TOKEN) {
    console.warn('[Analytics] VITE_MIXPANEL_TOKEN is not set — tracking disabled')
    return
  }
  // Autocapture and automatic pageview tracking are off — every event billed against
  // the plan should be one we deliberately instrumented. See
  // docs/Behavioral_Analysis_Mixpanel.md §1 (Core decisions — Autocapture).
  mixpanel.init(MIXPANEL_TOKEN, {
    autocapture: false,
    debug: isDev,
    track_pageview: false,
    persistence: 'localStorage',
  })
  isInitialized = true
}

export function useAnalytics() {
  // useAnalytics() is also called from outside any component's setup() — the router
  // guard (src/router/guards/stageTracking.js) and Pinia store actions (src/stores/auth.js)
  // — where useRoute()'s underlying inject() has no component instance to read. Only call
  // it when one exists; callers without route context (the guard) already pass an explicit
  // `module` in their track() properties, which overrides this via the merge order below.
  // See docs/Behavioral_Analysis_Mixpanel.md §4a.
  const route = getCurrentInstance() ? useRoute() : null
  const authStore = useAuthStore()

  function getBaseProperties() {
    const { organization, organizationDescription, role, orgStatus, parentCompany } = authStore
    return {
      organization: organization ?? null,
      organization_description: organizationDescription ?? null,
      role: role ?? null,
      module: route?.meta?.moduleCode ?? null,
      org_status: orgStatus ?? null,
      ...(parentCompany ? { parent_company: parentCompany } : {}),
    }
  }

  function identify(userId, userProperties = {}) {
    if (!isInitialized) return
    mixpanel.identify(String(userId))
    mixpanel.people.set(userProperties)
  }

  function setSuperProperties({ id, name, role }) {
    if (!isInitialized) return
    mixpanel.register({ user_id: id, user_name: name, user_role: role })
  }

  function track(eventName, properties = {}) {
    if (!isInitialized) return
    if (isInternalOrg(authStore.orgStatus)) return
    mixpanel.track(eventName, { ...getBaseProperties(), ...properties })
  }

  function timeEvent(eventName) {
    if (!isInitialized) return
    mixpanel.time_event(eventName)
  }

  function reset() {
    if (!isInitialized) return
    mixpanel.reset()
  }

  return { identify, setSuperProperties, track, timeEvent, reset }
}
